/**
 * Pause menu, persisted settings and quality presets as a feature (plan G12, spec §8, §10). Replaces G6's M0
 * pause port.
 *
 * - `init`: loads the saved settings (`localStorage`, forgiving), applies them (input bindings, the player's
 *   flight-assist state) and the quality preset (`?quality=` overrides it for testing without being saved),
 *   then announces them on the bus.
 * - `bus 'pause'` opens the menu (the overlay words the reason), `bus 'resume'` closes it. Resume is emitted
 *   synchronously from the click or key press, so the loop can take pointer lock inside that gesture.
 * - Keyboard: a capture-phase listener handles menu keys while the menu is open and stops them reaching the
 *   flight bindings. Gamepad: `frame` reads D-pad, left stick, A, B and Start through `stepPadNav`.
 * - `afterTick`: the player's flight assist is set to `defaultFA` whenever it spawns or respawns.
 * Setting changes are saved immediately, applied live and announced with `bus 'settings'`.
 * - D-617 (spec §4): the 8 system rows; Save & Load over the career's slots (manual save anywhere outside combat);
 *   Quit to title autosaves first. A modal screen over the menu (away report, Registrant) hides it (`onMenuCover`).
 * - Dashboard row (W3 card M4): opens the real dashboard and resumes in the same gesture; when the menu is open over
 *   the dashboard (Esc at its top, P) the row reads *Close dashboard*: it closes the dashboard and resumes to flight.
 * - Settings › Interface (spec 2026-10-09 §5.6, card F2): UI scale, text contrast and reduced motion apply to <html>
 *   (`--ui-user-scale`, `ui-high-contrast`, `ui-reduced-motion`) at init, on every change and when the OS motion
 *   preference changes. HUD labels reach the Reach HUD and Dashboard time the dashboard through `bus 'settings'`.
 */
import type { GameContext, GameFeature, PauseReason, Settings } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { logger } from '../../core/logger'
import { combatGuardConfig, playerInCombat, watchPlayerHits, type CombatGuard } from './combat'
import helpJson from './help.json'
import { fill, lastSaveLine, menuSaveSlots, saveBlockReason } from './saves'
import menuText from '../../hud/menu.json'
import { controlsReference, loadAllBindings, shippedControlsKeys } from '../../hud/controlsRef'
import {
  closeMenu,
  createMenuModel,
  focusSlotAction,
  onMenuCover,
  setSaveSlots,
  createPadNav,
  currentItems,
  disarmPadNav,
  focusItem,
  keyToMenuInput,
  menuInput,
  openMenu,
  setSelectValue,
  setSliderValue,
  stepPadNav,
  type MenuAction,
  type MenuInput,
  type MenuModel,
  type PadNavState,
} from '../../hud/menu'
import '../../hud/menu.css'
import type { QualityPreset } from '../../render/contracts'
import { applyQuality, loadQualityTable, parseQualityOverride } from '../../render/quality'
import { applyInputSettings, applyInterfaceSettings, captureInputBase, playerSpawnedSince, type InputBase } from '../../settings/apply'
import { browserStorage, loadSettings, loadSettingsConfig, saveSettings, type StorageLike } from '../../settings/settings'
import { Ship, Weapons } from '../../world/components'
import { routeCoverEscape } from './coverEscape'
import { createMenuView, type MenuView } from './view'
import type { CareerService } from '../../career/flow'
import type { M2Runtime } from '../../space/contracts'
import type { SaveSlotId, ManualSlotId } from '../../ui/contracts'
import { MANUAL_SLOTS, SAVE_SLOTS } from '../../ui/contracts'
import { closeDashboard, dashboardOpen, openDashboard } from '../../ui/dashboard/registry'

const REASONS: Readonly<Record<PauseReason, string>> = {
  user: 'Paused',
  hidden: 'Paused',
  blur: 'Paused',
  pointerLock: 'Paused',
  gamepad: 'Controller disconnected',
}

interface MenuRuntime {
  ctx: GameContext
  model: MenuModel
  view: MenuView
  nav: PadNavState
  storage: StorageLike | null
  base: InputBase
  /** The saved quality while a `?quality=` override is in effect (null = no override). */
  savedQuality: QualityPreset | null
  cursor: number
  offs: (() => void)[]
  /** Hits on the player and hostiles nearby: manual save is refused in combat (D-617 Q5). */
  guard: CombatGuard
}

const ST = menuText.saves

/** The career (M2) service; Quit to title needs the additive `autosave` and `showTitle` (career mode only). */
type MenuCareer = M2Runtime & Partial<Pick<CareerService, 'autosave' | 'showTitle'>>

function careerService(r: MenuRuntime): MenuCareer | undefined {
  return r.ctx.services.maybe('m2')
}

function inCombat(r: MenuRuntime): boolean {
  return playerInCombat(r.guard, r.ctx.sim)
}

/** Rebuilds the save slot rows from the career's slot list (and the standing note) while the saves screen shows. */
function refreshSaves(r: MenuRuntime): void {
  const svc = careerService(r)
  if (svc === undefined) {
    setSaveSlots(r.model, [])
    r.view.note(ST.noCareer)
    return
  }
  const state = svc.ui.getState()
  setSaveSlots(r.model, menuSaveSlots(svc.data, state, inCombat(r)))
  r.view.note(saveBlockReason(state, inCombat(r)) ?? '')
}

/** Screen-specific content after a screen change. */
function enteredScreen(r: MenuRuntime): void {
  r.view.note('')
  if (r.model.screen === 'saves') refreshSaves(r)
  if (r.model.screen === 'quit') {
    const svc = careerService(r)
    r.view.note(svc === undefined ? ST.noTitle : lastSaveLine(svc.ui.getState().saves, Date.now()))
  }
}

function isSlot(id: string): id is SaveSlotId {
  return (SAVE_SLOTS as readonly string[]).includes(id)
}

function isManual(id: string): id is ManualSlotId {
  return (MANUAL_SLOTS as readonly string[]).includes(id)
}

function downloadText(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

function failed(r: MenuRuntime, message: string): void {
  r.view.notice(fill(ST.failed, { error: message }))
}

/** Runs the model's request (save, load, export, quit): async storage work, then the menu updates. */
function runRequest(r: MenuRuntime): void {
  const req = r.model.request
  r.model.request = null
  const svc = careerService(r)
  if (req === null) return
  if (svc === undefined) {
    r.view.notice(req.kind === 'quit' ? ST.noTitle : ST.noCareer)
    return
  }
  const actions = svc.ui.getState().actions
  if (req.kind === 'quit') {
    const { autosave, showTitle } = svc
    if (autosave === undefined || showTitle === undefined) {
      r.view.notice(ST.noTitle)
      return
    }
    void autosave().then((res) => {
      if (!res.ok) {
        r.view.notice(fill(ST.quitFailed, { error: res.error.message }))
        return
      }
      showTitle()
      // The title hold keeps the sim and the pointer free; resuming only clears the pause.
      r.ctx.bus.emit('resume', undefined)
    })
    return
  }
  if (!isSlot(req.slot)) return
  const slot = req.slot
  const label = r.model.saveSlots.find((i) => i.id === `slot:${slot}`)?.label ?? slot
  if (req.kind === 'save') {
    // Re-checked at the press: combat may have started since the screen opened.
    const blocked = saveBlockReason(svc.ui.getState(), inCombat(r))
    if (blocked !== null || !isManual(slot)) {
      r.view.notice(blocked ?? ST.manualOnly)
      return
    }
    void actions.saveTo(slot).then((res) => {
      if (res.ok) r.view.notice(fill(ST.saved, { slot: label }))
      else failed(r, res.error.message)
      if (r.model.open && r.model.screen === 'saves') {
        refreshSaves(r)
        r.view.render()
      }
    })
    return
  }
  if (req.kind === 'load') {
    void actions.loadSlot(slot).then((res) => {
      if (!res.ok) {
        failed(r, res.error.message)
        return
      }
      // The career enters docked (a hold): resuming only clears the pause.
      r.ctx.bus.emit('resume', undefined)
    })
    return
  }
  void actions.exportSave(slot).then((exp) => {
    if (exp === null) {
      r.view.notice(ST.emptySlot)
      return
    }
    downloadText(exp.fileName, exp.text)
    r.view.notice(fill(ST.exported, { file: exp.fileName }))
  })
}

let rt: MenuRuntime | null = null

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/** The OS reduced-motion media query, or null where there is no `matchMedia` (tests, old engines). */
function reducedMotionQuery(): MediaQueryList | null {
  return typeof matchMedia === 'function' ? matchMedia(REDUCED_MOTION_QUERY) : null
}

/** UI scale, text contrast and reduced motion onto <html> (the tokens' CSS reads them). UI events only. */
function applyInterface(ctx: GameContext): void {
  if (typeof document === 'undefined') return
  applyInterfaceSettings(document.documentElement, ctx.settings, reducedMotionQuery()?.matches === true)
}

/** The player's aim-assist strength follows the setting (AI pilots keep Standard from spawn). */
function setPlayerAssist(ctx: GameContext): void {
  const p = ctx.sim.playerEid
  if (p >= 0) Weapons.assist[p] = ctx.data.weapons.aimAssist.levels[ctx.settings.aimAssist]
}

function setPlayerFA(ctx: GameContext): void {
  const p = ctx.sim.playerEid
  if (p >= 0) Ship.faOn[p] = ctx.settings.defaultFA ? 1 : 0
}

/** Persists, applies and announces the setting the model just changed. */
function commit(r: MenuRuntime, key: keyof Settings | null): void {
  const { ctx } = r
  if (key === 'quality') r.savedQuality = null
  const toSave: Settings = r.savedQuality === null ? ctx.settings : { ...ctx.settings, quality: r.savedQuality }
  if (!saveSettings(r.storage, toSave)) r.view.notice('Settings apply now but cannot be saved in this browser.')
  applyInputSettings(ctx.data.bindings, r.base, ctx.settings)
  applyInterface(ctx)
  if (key === 'quality' && ctx.settings.quality !== ctx.quality) {
    logger.info(`Quality preset: ${ctx.settings.quality}`)
    applyQuality(ctx, ctx.settings.quality)
  }
  ctx.bus.emit('settings', ctx.settings)
}

/**
 * The Dashboard row (spec §4, W3 card M4), inside the activating gesture. Over the dashboard: close it, then resume
 * (no modal or hold is left, so the resume takes the pointer lock in this gesture). Otherwise open it first, so its
 * modal and hold keep the pointer free when the resume runs; in flight only, like Tab (not docked or under a modal).
 */
function dashboardRow(r: MenuRuntime): void {
  const { ctx } = r
  if (dashboardOpen()) {
    closeDashboard(false)
    ctx.bus.emit('resume', undefined)
    return
  }
  if (ctx.holds.size === 0 && ctx.ui.modal === 0) openDashboard()
  if (!dashboardOpen()) {
    r.view.notice(menuText.dashboardUnavailable)
    return
  }
  ctx.bus.emit('resume', undefined)
}

function handle(r: MenuRuntime, action: MenuAction): void {
  switch (action) {
    case 'dashboard':
      dashboardRow(r)
      return
    case 'resume':
      // Synchronous inside the gesture: the loop requests pointer lock from this call stack.
      r.ctx.bus.emit('resume', undefined)
      return
    case 'screen':
      enteredScreen(r)
      r.view.render()
      return
    case 'request':
      runRequest(r)
      return
    case 'denied':
      if (r.model.denied !== null) r.view.notice(r.model.denied)
      r.view.sync()
      return
    case 'changed':
      commit(r, r.model.changed)
      r.view.sync()
      return
    case 'moved':
      r.view.sync()
      return
    case 'scrollUp':
      r.view.scroll(-1)
      return
    case 'scrollDown':
      r.view.scroll(1)
      return
    case 'none':
      return
  }
}

function input(r: MenuRuntime, i: MenuInput): void {
  handle(r, menuInput(r.model, i, r.ctx.settings))
}

function onKeyDown(e: KeyboardEvent): void {
  const r = rt
  // H8 (card A4): Esc closes a modal screen opened over the menu; consumed, it reaches nothing else.
  if (r && routeCoverEscape(r.ctx.ui, e.code, e.repeat, r.model.open)) {
    e.preventDefault()
    e.stopImmediatePropagation()
    return
  }
  if (!r || !r.model.open || !r.view.visible) return
  let i: MenuInput | null
  if (e.code === 'Tab') i = e.shiftKey ? 'up' : 'down'
  else i = keyToMenuInput(e.code, e.repeat)
  if (i === null) {
    // Swallow the menu's own keys on repeat too, so nothing leaks into the flight bindings.
    if (!e.repeat) return
    if (keyToMenuInput(e.code, false) === null) return
  }
  e.preventDefault()
  e.stopPropagation()
  if (i !== null) input(r, i)
}

function open(r: MenuRuntime, reason: PauseReason): void {
  // Esc at the dashboard's top level (or P) opens the menu over it: the Dashboard row then reads Close dashboard.
  openMenu(r.model, dashboardOpen())
  disarmPadNav(r.nav)
  r.view.show(REASONS[reason])
}

const feature: GameFeature = {
  name: 'menu',
  order: FEATURE_ORDER.menu,
  init(ctx: GameContext): void {
    const cfg = loadSettingsConfig()
    const table = loadQualityTable()
    const storage = browserStorage()
    const loaded = loadSettings(storage)
    // Mutate in place: features that initialised earlier may hold the ctx.settings reference.
    Object.assign(ctx.settings, loaded)

    const override = parseQualityOverride(location.search, table)
    let savedQuality: QualityPreset | null = null
    if (override !== null && override !== loaded.quality) {
      savedQuality = loaded.quality
      ctx.settings.quality = override
      logger.info(`Quality preset overridden by the URL: ${override}`)
    }

    const helpPages = [...helpJson.pages, helpJson.legend].map((pg) => ({ id: pg.id, title: pg.title, hint: pg.hint }))
    const model = createMenuModel(cfg.ranges, helpPages)
    const base = captureInputBase(ctx.data.bindings)
    const r: MenuRuntime = {
      ctx,
      model,
      view: createMenuView(ctx.overlayRoot, model, () => ctx.settings, controlsReference(loadAllBindings(ctx.data.bindings), shippedControlsKeys(ctx.data)), helpJson, {
        focus(index: number): void {
          if (focusItem(model, index)) r.view.sync()
        },
        activate(index: number): void {
          focusItem(model, index)
          input(r, 'activate')
        },
        slide(index: number, value: number): void {
          focusItem(model, index)
          const item = currentItems(model)[index]
          if (item.kind === 'slider' && setSliderValue(model, item, ctx.settings, value)) handle(r, 'changed')
          else r.view.sync()
        },
        select(index: number, value: string): void {
          focusItem(model, index)
          const item = currentItems(model)[index]
          if (item.kind === 'select' && setSelectValue(model, item, ctx.settings, value)) handle(r, 'changed')
          else r.view.sync()
        },
        slotAction(index: number, action: number): void {
          focusSlotAction(model, index, action)
          input(r, 'activate')
        },
      }),
      nav: createPadNav(cfg.menuNav, ctx.data.bindings.gamepad.buttons.pause),
      storage,
      base,
      savedQuality,
      cursor: ctx.sim.events.head,
      offs: [],
      guard: { cfg: combatGuardConfig(), lastHitAt: Number.NEGATIVE_INFINITY },
    }
    rt = r

    applyInputSettings(ctx.data.bindings, base, ctx.settings)
    applyInterface(ctx)
    setPlayerFA(ctx)
    if (ctx.settings.quality !== ctx.quality) applyQuality(ctx, ctx.settings.quality, undefined, table)
    ctx.bus.emit('settings', ctx.settings)

    window.addEventListener('keydown', onKeyDown, { capture: true })
    const motion = reducedMotionQuery()
    if (motion !== null) {
      // "Follow OS": track the system preference live.
      const onMotion = (): void => applyInterface(ctx)
      motion.addEventListener('change', onMotion)
      r.offs.push(() => motion.removeEventListener('change', onMotion))
    }
    r.offs.push(
      // A modal screen over the menu: hide the menu under it (no bleed-through, audit §3.1 item 1).
      onMenuCover((active) => r.view.cover(active)),
      ctx.bus.on('pause', (reason) => open(r, reason)),
      ctx.bus.on('resume', () => {
        closeMenu(model)
        r.view.hide()
      }),
    )
  },
  afterTick(ctx: GameContext): void {
    const r = rt
    if (!r) return
    const ring = ctx.sim.events
    if (playerSpawnedSince(ring, r.cursor, ctx.sim.playerEid)) setPlayerFA(ctx)
    watchPlayerHits(r.guard, ctx.sim, r.cursor)
    // Every tick (cheap, idempotent): covers spawns, respawns, ship swaps and live setting changes.
    setPlayerAssist(ctx)
    r.cursor = ring.head
  },
  frame(ctx: GameContext, f): void {
    const r = rt
    if (!r) return
    // Every frame, open or not, so the press that opened the menu is not seen as a new edge.
    const i = stepPadNav(r.nav, ctx.input.pad, f.dt)
    if (i !== null && r.model.open && r.view.visible) input(r, i)
  },
  dispose(): void {
    window.removeEventListener('keydown', onKeyDown, { capture: true })
    if (rt) for (const off of rt.offs) off()
    rt = null
  },
}

export default feature
